import { Copyable } from "../../common_util";
import { Product, SelectionGroup } from "./catalog";
import { CartItem, CartItemSelectionGroup } from "./cart";
import { IdTarget } from "src/storefront/types/id_target";

export interface IModalControls {
  showModal: (product: Product, updateHistory: boolean) => void;
}

export class ProductModalProduct extends Copyable {
  readonly product: Product;
  readonly mealPreferences: string;
  readonly quantity: number;
  readonly selectionGroups: ProductModalSelectionGroup[];
  readonly idTarget: IdTarget;

  constructor(
    product: Product,
    mealPreferences: string,
    quantity: number,
    selectionGroups: ProductModalSelectionGroup[],
  ) {
    super();

    this.product = product;
    this.mealPreferences = mealPreferences;
    this.quantity = quantity;
    this.selectionGroups = selectionGroups;
    this.idTarget = new IdTarget();
  }

  static fromCartItem(product: Product, cartItem: CartItem): ProductModalProduct {
    const nestedSelectionGroups = product.kind == "branch" ? cartItem.selectionGroups.map(cartSelectionGroup => {
      const selectionGroup = product.selectionGroups.find(sg => sg.key == cartSelectionGroup.key);
      if (!selectionGroup) throw "This cart isn't compatible with this catalog";

      return ProductModalSelectionGroup.fromCartItemSelectionGroup(selectionGroup, cartSelectionGroup)
    }) : [];

    return new ProductModalProduct(
      product,
      cartItem.mealPreferences || "",
      cartItem.quantity,
      nestedSelectionGroups,
    );
  }

  static fromProduct(product: Product): ProductModalProduct {
    const quantity = product.defaultQuantity || 1;
    const selectionGroups = product.kind == "branch" ? product.selectionGroups.map(sg => ProductModalSelectionGroup.fromSelectionGroup(sg)) : []

    return new ProductModalProduct(product, "", quantity, selectionGroups);
  }

  isValid(): boolean {
    if (this.quantity < 1)
      return false;

    if (this.product.kind == "leaf")
      return true;

    return this.product.selectionGroups.every(sg => {
      const productModalSelectionGroup = this.selectionGroups.find(s => s.selectionGroup.key == sg.key);
      return productModalSelectionGroup?.isValid() ?? !sg.isRequired();
    });
  }

  subtotal(opts?: { excludeSelf?: boolean }) : number {
    const es : boolean = opts?.excludeSelf ?? false;
    return (((es ? 0 : this.product.price) + this.selectionGroups.reduce((accl, sg) => sg.subtotal() + accl, 0)) * this.quantity);
  }

  toCartItem(existingRef?: IdTarget): CartItem {
    const cartItemSelectionGroups = this.selectionGroups.map(sg => sg.toCartItemSelectionGroup());

    return CartItem.fromProduct(this.product).copyWith({
      mealPreferences: this.mealPreferences,
      quantity: this.quantity,
      selectionGroups: cartItemSelectionGroups,
      idTarget: existingRef ?? new IdTarget(),
    });
  }
}

export class ProductModalSelectionGroup extends Copyable {
  readonly selectionGroup: SelectionGroup;
  readonly productSelections: ProductModalProduct[];

  constructor(
    selectionGroup: SelectionGroup,
    productSelections: ProductModalProduct[],
  ) {
    super();

    this.selectionGroup = selectionGroup;
    this.productSelections = productSelections;
  }

  static fromCartItemSelectionGroup(selectionGroup: SelectionGroup, cartItemSelectionGroup: CartItemSelectionGroup): ProductModalSelectionGroup {
    const nestedProducts = cartItemSelectionGroup.cartItems.map(cartItem => {
      const product = selectionGroup.products.find(p => p.key == cartItem.key);

      if (!product) throw "This cart isn't compatible with this catalog";

      return ProductModalProduct.fromCartItem(product, cartItem);
    });

    return new ProductModalSelectionGroup(
      selectionGroup,
      nestedProducts,
    );
  }

  static fromSelectionGroup(sg: SelectionGroup): ProductModalSelectionGroup {
    const products = sg.products.filter(p => {
      return (p.defaultQuantity ?? 0) > 0;
    }).map(p => ProductModalProduct.fromProduct(p));

    return new ProductModalSelectionGroup(sg, products);
  }

  totalSelections(): number {
    return this.productSelections.reduce((total, pmp) => total + pmp.quantity, 0);
  }

  isBelowMinimum(): boolean {
    return this.totalSelections() < this.selectionGroup.minSelections;
  }

  isAboveMaximum(): boolean {
    const max = this.selectionGroup.maxSelections ?? Number.POSITIVE_INFINITY;
    return this.totalSelections() > max;
  }

  reachedMaxThreshold(): boolean {
    const max = this.selectionGroup.maxSelections ?? Number.POSITIVE_INFINITY;
    return this.totalSelections() >= max;
  }

  reachedMinThreshold(): boolean {
    return this.totalSelections() >= this.selectionGroup.minSelections;
  }

  isValid(): boolean {
    if (this.isBelowMinimum())
      return false;

    if (this.isAboveMaximum())
      return false;

    return true;
  }

  subtotal(): number {
    return this.productSelections.reduce((accl, sg) => accl + sg.subtotal(), 0);
  }

  toCartItemSelectionGroup(): CartItemSelectionGroup {
    const cartItems = this.productSelections.map(ps => ps.toCartItem());

    return CartItemSelectionGroup.fromSelectionGroup(this.selectionGroup).copyWith({
      cartItems: cartItems,
    });
  }
}
